import React from "react";
import { Bitcoin, Shield, Zap } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { JsonLdFaq } from "../components/SEO/JsonLd";

export default function About() {
  const faqItems = [
    {
      question: "What is Lo-Fee.com?",
      answer:
        "Lo-Fee.com is a comparison tool that helps Canadians find the lowest fees when buying or selling Bitcoin across different cryptocurrency exchanges in Canada.",
    },
    {
      question: "How does Lo-Fee.com calculate exchange fees?",
      answer:
        "We analyze trading fees, spreads, deposit fees, withdrawal fees, and network transaction costs across major Canadian exchanges to provide accurate, real-time comparisons.",
    },
    {
      question: "Is Lo-Fee.com free to use?",
      answer:
        "Yes, Lo-Fee.com is completely free for all users. We earn through affiliate relationships with some exchanges when users sign up through our links.",
    },
    {
      question: "How often are exchange rates updated?",
      answer:
        "All Bitcoin prices and exchange rates on Lo-Fee.com are updated every 60 seconds to ensure you're seeing the most current data.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          About Lo-Fee.com | Canadian Bitcoin Exchange Fee Comparison Tool
        </title>
        <meta
          name="description"
          content="Learn about Lo-Fee.com, your trusted resource for comparing cryptocurrency exchange fees in Canada. Find out how we help Canadians make informed Bitcoin trading decisions."
        />
        <link rel="canonical" href="https://lo-fee.com/about" />
      </Helmet>
      <JsonLdFaq faqs={faqItems} />

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="space-y-12">
          <header className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              About Lo-Fee.com
            </h1>
            <p className="text-lg text-[--text-secondary] max-w-3xl">
              Your trusted resource for comparing cryptocurrency exchange fees
              in Canada. We help Canadians make informed decisions when buying
              or selling Bitcoin.
            </p>
          </header>

          <section className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Bitcoin className="h-6 w-6 text-blue-500" />,
                title: "Real-Time Data",
                description:
                  "Live price updates and fee calculations across all major Canadian exchanges.",
              },
              {
                icon: <Shield className="h-6 w-6 text-green-500" />,
                title: "Transparent Comparison",
                description:
                  "Clear breakdown of trading fees, spreads, and payment method restrictions.",
              },
              {
                icon: <Zap className="h-6 w-6 text-yellow-500" />,
                title: "Instant Updates",
                description:
                  "Exchange rates and fees are automatically updated every 60 seconds.",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="space-y-2 p-6 bg-[--bg-secondary] border-2 border-[--border-color]"
              >
                {feature.icon}
                <h3 className="text-lg font-bold">{feature.title}</h3>
                <p className="text-[--text-secondary]">{feature.description}</p>
              </div>
            ))}
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Our Mission</h2>
            <div className="prose prose-lg max-w-none text-[--text-secondary]">
              <p>
                At Lo-Fee.com, we believe that everyone should have access to
                transparent information about cryptocurrency exchange fees. Our
                mission is to help Canadians save money and make informed
                decisions when trading Bitcoin.
              </p>
              <p>We achieve this by:</p>
              <ul>
                <li>
                  Providing real-time fee comparisons across major exchanges
                </li>
                <li>Explaining different fee structures and payment methods</li>
                <li>Updating exchange rates and fees automatically</li>
                <li>
                  Maintaining a user-friendly interface for easy comparison
                </li>
              </ul>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">How We Compare</h2>
            <div className="grid sm:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-lg font-bold">Included in Calculations</h3>
                <ul className="space-y-2 text-[--text-secondary]">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Trading fees and
                    spreads
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Deposit and
                    withdrawal fees
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Network
                    transaction costs
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Payment method
                    restrictions
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-bold">Additional Considerations</h3>
                <ul className="space-y-2 text-[--text-secondary]">
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Exchange
                    verification requirements
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Platform
                    reliability and uptime
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Customer support
                    quality
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Security features
                    and history
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-[--bg-secondary] border-2 border-[--border-color] p-8">
            <div className="max-w-3xl mx-auto text-center space-y-4">
              <h2 className="text-2xl font-bold">Contact Us</h2>
              <p className="text-[--text-secondary]">
                Have questions or feedback? Email us at{" "}
                <a
                  href="mailto:contact@lo-fee.com"
                  className="text-[--accent] hover:underline"
                >
                  contact@lo-fee.com
                </a>
              </p>
              <p className="text-sm text-[--text-secondary]">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
